"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect, useId } from 'react';
import { validateSurface } from '../../globals.js';
import { BadgeIndicator } from '../../badges/badge-indicator/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import styles from './button-icon.module.scss.js';

/**
 * @component ButtonIcon
 * @package ButtonIcon
 * @document ../_docs/code/ButtonIcon.editor.tsx
 * @document ../_docs/code/ButtonIcon.snippet.tsx
 * @document ../_docs/code/ButtonIcon.import.tsx
 */
const ButtonIcon = forwardRef((_a, ref) => {
    var { kind = 'ghost', size = 'large', surface: surfaceProp = 'lightPrimary', showBorder = false, focusBorderPosition = 'outside', floating = false, iconOffset, fitToIcon = false, disabled = false, tabIndex = 0, ariaLabel, href, renderIcon, renderSelectedIcon, selectable = false, selected, onClick, onChange, onKeyDown, inputId, role, ariaHidden = false, showBadgeIndicator = false, badgeIndicator, name, value, 'data-testid': testId = 'icon-button' } = _a, rest = __rest(_a, ["kind", "size", "surface", "showBorder", "focusBorderPosition", "floating", "iconOffset", "fitToIcon", "disabled", "tabIndex", "ariaLabel", "href", "renderIcon", "renderSelectedIcon", "selectable", "selected", "onClick", "onChange", "onKeyDown", "inputId", "role", "ariaHidden", "showBadgeIndicator", "badgeIndicator", "name", "value", 'data-testid']);
    const [isHover, setIsHover] = useState(false);
    const [isToggled, setIsToggled] = useState(false);
    const toggleTimer = useRef(null);
    const [isIconSelected, setIsIconSelected] = useState(!!selected);
    const surface = validateSurface(surfaceProp, 'ButtonIcon');
    useEffect(() => {
        /* istanbul ignore if -- @preserve */
        if (toggleTimer.current) {
            clearTimeout(toggleTimer.current);
        }
        toggleTimer.current = setTimeout(() => {
            setIsToggled(false);
        });
    }, [isIconSelected]);
    useEffect(() => {
        setIsIconSelected(selected);
    }, [selected]);
    const isControlled = selected !== undefined;
    const _useId = useId();
    const checkboxId = useRef(undefined);
    if (!checkboxId.current)
        checkboxId.current = inputId || name || _useId;
    const _onClick = (e) => {
        e.stopPropagation();
        const newEvent = e;
        newEvent.target = e.currentTarget;
        /* istanbul ignore else -- @preserve */
        if (onClick)
            return onClick(newEvent);
    };
    const _onButtonKeyDown = (e) => {
        const keySelected = e.key;
        const enterOrSpace = keySelected === 'Enter' || keySelected === ' ';
        if (enterOrSpace) {
            e.stopPropagation();
        }
        /* istanbul ignore if -- @preserve */
        if (onKeyDown)
            onKeyDown(e);
    };
    const handleSelect = (e) => {
        setIsToggled(true);
        if (selectable && onChange) {
            onChange(e, { name, value, selected: !selected });
        }
        if (!isControlled) {
            setIsIconSelected(!isIconSelected);
        }
    };
    const _onInputKeyDown = (e) => {
        e.stopPropagation();
        const { key } = e;
        const enterOrSpace = key === 'Enter' || key === ' ';
        if (enterOrSpace) {
            e.preventDefault();
            if (onKeyDown)
                onKeyDown(e);
            handleSelect(e);
        }
    };
    const handleInputClick = (e) => {
        var _a;
        e.stopPropagation();
        // if (disabled) return;
        /* istanbul ignore if -- @preserve */
        if (e.type === 'click' && isHover) {
            if (typeof document !== 'undefined' &&
                document.activeElement instanceof HTMLElement)
                (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur();
        }
    };
    const getContainerSize = (sizeContainer, Iconkind = 'ghost', fitToIconContainer = false) => {
        const sizeArray = ['large', 'small'];
        const customSize = (sizeContainer === null || sizeContainer === void 0 ? void 0 : sizeContainer.length) > 0 && !sizeArray.includes(sizeContainer);
        const _calcRem = (smallSize, largeSize) => {
            if (sizeContainer === 'small') {
                return calculateRem(smallSize);
            }
            return calculateRem(largeSize);
        };
        let containerSize = customSize
            ? calculateRem(sizeContainer)
            : _calcRem(32, 44);
        if (fitToIconContainer && Iconkind.toLowerCase() === 'ghost') {
            containerSize = customSize
                ? calculateRem(sizeContainer)
                : _calcRem(20, 24);
        }
        return containerSize;
    };
    const buttonIconContainerStyles = [styles.buttonIconContainer];
    const buttonIconStyles = [
        styles.buttonIcon,
        kind.toLowerCase() === 'ghost' && fitToIcon
            ? styles['fitToIcon']
            : undefined,
        styles[kind.toLowerCase()],
        styles[disabled ? 'disabled' : ''],
        styles[`focus-${floating && kind !== 'ghost' ? 'inside' : focusBorderPosition}`],
        styles[(showBorder && kind === 'lowContrastSolid') ||
            (kind === 'lowContrastSolid' && surface.includes('Primary'))
            ? 'showBorder'
            : ''],
        styles[floating && kind !== 'ghost' ? 'floating' : ''],
        styles[iconOffset ? 'iconOffset' : ''],
    ];
    buttonIconStyles.push(styles[surface.toLowerCase()]);
    if (selectable)
        buttonIconStyles.push(styles.selectable);
    const BadgeIndicatorContainer = [styles.badgeIndicatorContainer];
    const hitAreaStyles = [styles.hitArea];
    const buttonType = rest.type ? rest.type : 'button'; // Needs button type to default to 'button' so that when used in a form, it does not act as 'type=submit'.
    if (badgeIndicator === null || badgeIndicator === void 0 ? void 0 : badgeIndicator.expandDirection) {
        BadgeIndicatorContainer.push(styles[badgeIndicator === null || badgeIndicator === void 0 ? void 0 : badgeIndicator.expandDirection], styles[disabled ? 'disabled' : '']);
    }
    let iconContainerStyles;
    if (!href) {
        iconContainerStyles = { left: iconOffset === null || iconOffset === void 0 ? void 0 : iconOffset.x, bottom: iconOffset === null || iconOffset === void 0 ? void 0 : iconOffset.y };
    }
    const _calcIconSize = () => {
        /* istanbul ignore if -- @preserve */
        if (typeof size === 'number') {
            return size;
        }
        switch (kind) {
            case 'ghost':
                return size === 'small' ? 'medium' : 'large';
            default:
                return size === 'small' ? 'small' : 'medium';
        }
    };
    const getIconColor = () => {
        if (kind === 'highContrast') {
            return surface.includes('dark')
                ? 'var(--icon-color-dark)'
                : 'var(--icon-color-light)';
        }
        if (surface.includes('dark')) {
            return 'var(--icon-color-light)';
        }
        return 'var(--icon-color-dark)';
    };
    const _renderIcon = () => {
        if (!renderIcon) {
            return null;
        }
        const IconComponent = renderIcon({
            size: _calcIconSize(),
            color: getIconColor(),
        });
        return IconComponent.type && IconComponent;
    };
    const _renderSelectedIcon = () => {
        if (!renderSelectedIcon) {
            return _renderIcon();
        }
        const IconComponent = renderSelectedIcon({
            size: _calcIconSize(),
            color: getIconColor(),
        });
        return IconComponent.type && IconComponent;
    };
    function getOffset() {
        var _a, _b, _c, _d;
        let dafaultValue = { x: '0px', y: '0px' };
        const badgeIndictorOffSetConfig = {
            ghost: {
                large: { x: calculateRem('1px'), y: calculateRem('1px') },
                small: { x: calculateRem('1px'), y: '0px' },
            },
            lowContrastTonal: {
                large: { x: calculateRem('6px'), y: calculateRem('6px') },
                small: { x: calculateRem('4px'), y: calculateRem('4px') },
            },
            lowContrastSolid: {
                large: { x: calculateRem('6px'), y: calculateRem('6px') },
                small: { x: calculateRem('4px'), y: calculateRem('4px') },
            },
            highContrast: {
                large: { x: calculateRem('6px'), y: calculateRem('6px') },
                small: { x: calculateRem('4px'), y: calculateRem('4px') },
            },
        };
        if (size === 'small' || size === 'large') {
            dafaultValue = badgeIndictorOffSetConfig[kind][size];
        }
        return {
            x: badgeIndicator && ((_a = badgeIndicator.offset) === null || _a === void 0 ? void 0 : _a.x)
                ? (_b = badgeIndicator.offset) === null || _b === void 0 ? void 0 : _b.x
                : dafaultValue.x,
            y: badgeIndicator && ((_c = badgeIndicator.offset) === null || _c === void 0 ? void 0 : _c.y)
                ? (_d = badgeIndicator.offset) === null || _d === void 0 ? void 0 : _d.y
                : dafaultValue.y,
        };
    }
    const _calcBadgeBorder = () => {
        if (badgeIndicator && badgeIndicator.showBorder !== undefined) {
            return badgeIndicator.showBorder;
        }
        if (kind !== 'ghost') {
            return true;
        }
        return undefined;
    };
    const badgeIndicatorOffset = getOffset();
    const btnIconStyleObj = {
        height: getContainerSize(size, kind, fitToIcon),
        width: getContainerSize(size, kind, fitToIcon),
        '--ghost-fit-icon': getContainerSize(size),
    };
    const _getNumberedBadgeIndicatorSize = () => {
        const sizeMap = {
            small: 16,
            medium: 18,
            large: 20,
            xlarge: 24,
            '2xlarge': 28,
        };
        if (badgeIndicator && badgeIndicator.kind === 'numbered') {
            return badgeIndicator.size ? sizeMap[badgeIndicator.size] : 16;
        }
        return 16;
    };
    const _renderBadgeIndicator = () => (jsx("span", { style: {
            width: _getNumberedBadgeIndicatorSize(),
            height: _getNumberedBadgeIndicatorSize(),
            left: `calc(50% + ${badgeIndicatorOffset.x === 0 || badgeIndicatorOffset.x === '0'
                ? '0px'
                : calculateRem(badgeIndicatorOffset.x)})`,
            bottom: `calc(50% + ${badgeIndicatorOffset.y === 0 || badgeIndicatorOffset.y === '0'
                ? '0px'
                : calculateRem(badgeIndicatorOffset.y)})`,
        }, className: BadgeIndicatorContainer.join(' '), children: jsx(BadgeIndicator, Object.assign({ maximumDigits: 2, showBorder: _calcBadgeBorder(), ariaHidden: true, surface: surface }, badgeIndicator)) }));
    if (href) {
        return (jsxs("span", { className: buttonIconContainerStyles.join(' '), style: btnIconStyleObj, children: [showBadgeIndicator && !disabled && _renderBadgeIndicator(), jsxs("a", Object.assign({}, rest, { onClick: _onClick, "data-testid": testId, className: buttonIconStyles.join(' '), "aria-label": ariaLabel || 'button icon', role: role || 'link', href: href, "aria-disabled": disabled, "aria-hidden": ariaHidden, children: [!disabled && jsx("span", { className: hitAreaStyles.join(' ') }), jsx("span", { style: iconContainerStyles, className: styles.iconContainer, children: _renderIcon() })] }))] }));
    }
    if (selectable) {
        return (jsxs("span", { className: buttonIconContainerStyles.join(' '), style: btnIconStyleObj, onMouseOver: () => {
                setIsHover(true);
            }, onMouseLeave: () => {
                setIsHover(false);
            }, onFocus: () => {
                setIsHover(false);
            }, children: [showBadgeIndicator && !disabled && _renderBadgeIndicator(), jsxs("label", Object.assign({}, rest, { className: buttonIconStyles.join(' '), htmlFor: checkboxId.current, "aria-label": ariaLabel || 'selectable button icon', children: [jsx(ScreenReaderText, { "aria-live": "assertive", children: isToggled && `${ariaLabel || 'selectable button icon'}` }), jsx("input", { className: styles.toggleInput, type: "checkbox", "data-testid": testId, disabled: disabled, "aria-disabled": disabled, checked: isIconSelected, id: checkboxId.current, "aria-checked": isIconSelected, name: name, tabIndex: tabIndex || 0, onChange: handleSelect, onKeyDown: _onInputKeyDown, onClick: handleInputClick }), !disabled && jsx("span", { className: hitAreaStyles.join(' ') }), jsx("span", { style: iconContainerStyles, className: styles.iconContainer, children: isIconSelected ? _renderSelectedIcon() : _renderIcon() })] }))] }));
    }
    return (jsxs("span", { className: buttonIconContainerStyles.join(' '), style: btnIconStyleObj, children: [showBadgeIndicator && !disabled && _renderBadgeIndicator(), jsxs("button", Object.assign({}, rest, { ref: ref, type: buttonType, className: buttonIconStyles.join(' '), "data-testid": testId, role: role || 'button', disabled: disabled, "aria-label": ariaLabel || 'icon', "aria-disabled": disabled, "aria-hidden": ariaHidden, onKeyDown: _onButtonKeyDown, onClick: _onClick, tabIndex: tabIndex, children: [!disabled && jsx("span", { className: hitAreaStyles.join(' ') }), jsx("span", { style: iconContainerStyles, className: styles.iconContainer, children: _renderIcon() })] }))] }));
});

export { ButtonIcon, ButtonIcon as default };
